import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { MapPin, Calendar, Play } from "lucide-react";
import { parseEngagementVenue, formatSeason } from "@/lib/galleryUtils";

interface Gallery {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  featured_image_url: string | null;
  gallery_type: string;
  event_date: string | null;
  venue: string | null;
  seo_title: string | null;
  seo_description: string | null;
}

const Galleries = () => {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [filter, setFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchGalleries();
  }, [filter]);

  const fetchGalleries = async () => {
    setIsLoading(true);
    let query = supabase
      .from("galleries")
      .select("*")
      .eq("is_published", true)
      .order("event_date", { ascending: false });

    if (filter !== "all") {
      query = query.eq("gallery_type", filter);
    }

    const { data } = await query;
    setGalleries(data || []);
    setIsLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>Wedding & Engagement Galleries | Pixie Clicks | NJ, FL & CO</title>
        <meta
          name="description"
          content="Browse beautiful wedding and engagement galleries. Real love stories captured at stunning venues in NJ, FL & CO."
        />
        <meta property="og:title" content="Wedding & Engagement Galleries | Pixie Clicks" />
        <meta property="og:description" content="Browse beautiful wedding and engagement galleries. Real love stories captured at stunning venues." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/galleries" />
        <link rel="canonical" href="https://pixieclicks.com/galleries" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Wedding & Engagement Galleries",
            "description": "Beautiful wedding and engagement galleries by Pixie Clicks.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Pixie Clicks",
              "areaServed": ["New Jersey", "Florida", "Colorado"]
            }
          })}
        </script>
      </Helmet>
      <Layout>
        {/* Hero */}
        <section className="py-20 md:py-28 bg-muted/30">
          <div className="container-wide text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-serif text-4xl font-semibold text-foreground md:text-5xl"
            >
              Real <span className="text-primary">Love Stories</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground"
            >
              Browse through beautiful moments we've captured
            </motion.p>
          </div>
        </section>

        {/* Filters */}
        <section className="py-8 border-b border-border">
          <div className="container-wide">
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                { value: "all", label: "All" },
                { value: "wedding", label: "Weddings" },
                { value: "engagement", label: "Engagements" },
                { value: "video", label: "Videos" },
              ].map((option) => (
                <Badge
                  key={option.value}
                  variant={filter === option.value ? "default" : "outline"}
                  className="cursor-pointer px-6 py-2 text-sm"
                  onClick={() => setFilter(option.value)}
                >
                  {option.label}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* Galleries Grid */}
        <section className="section-padding">
          <div className="container-wide">
            {isLoading ? (
              <div className="flex justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : galleries.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                No galleries yet. Check back soon!
              </div>
            ) : (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {galleries.map((gallery, index) => (
                  <motion.article
                    key={gallery.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group"
                  >
                    <Link to={`/galleries/${gallery.slug}`} className="block">
                      <div className="aspect-[3/4] overflow-hidden rounded-xl bg-muted relative">
                        {gallery.featured_image_url ? (
                          <img
                            src={gallery.featured_image_url}
                            alt={gallery.title}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center bg-muted">
                            <span className="text-muted-foreground">No image</span>
                          </div>
                        )}
                        {/* Play button overlay for video galleries */}
                        {gallery.gallery_type === "video" && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 shadow-lg group-hover:scale-110 transition-transform">
                              <Play className="h-8 w-8 text-foreground fill-current" />
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                          <Badge className="bg-primary text-primary-foreground capitalize">
                            {gallery.gallery_type}
                          </Badge>
                        </div>
                      </div>
                      <div className="mt-4">
                        <h2 className="font-serif text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                          {gallery.title}
                        </h2>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {gallery.gallery_type === "engagement" ? (
                            <>
                              {(() => {
                                const { location, season } = parseEngagementVenue(gallery.venue);
                                return (
                                  <>
                                    {location && (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="h-4 w-4" />
                                        {location}
                                      </span>
                                    )}
                                    {season && (
                                      <span className="flex items-center gap-1">
                                        <Calendar className="h-4 w-4" />
                                        {formatSeason(season)}
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </>
                          ) : (
                            <>
                              {gallery.venue && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  {gallery.venue}
                                </span>
                              )}
                              {gallery.event_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  {format(new Date(gallery.event_date), "MMM yyyy")}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Galleries;
